import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { clearRememberedActiveChatThread } from "../utils/chat_thread_persistence";
import {
  clearQueuedMessagesForThread,
  enqueueQueuedMessageForThread as enqueueQueuedMessageForThreadState,
  markQueuedMessageForThread as markQueuedMessageForThreadState,
  moveQueuedMessagesForThread,
  queuedMessagesForThread,
  removeQueuedMessageForThread as removeQueuedMessageForThreadState,
  shiftQueuedMessageForThread as shiftQueuedMessageForThreadState,
  updateQueuedMessageForThread as updateQueuedMessageForThreadState,
} from "../components/Chat/queuedChatMessages";
import { useLiveQuoteActions } from "./LiveQuoteContext";
const ChatContext = createContext(null);
const DATA_PROCESSES_PANEL_WIDTH_STORAGE_KEY = "incite:data-processes-panel-width:v2";

const safeLocalStorageGet = (key) => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage?.getItem(key) || null;
  } catch (_error) {
    return null;
  }
};

const safeLocalStorageSet = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage?.setItem(key, value);
  } catch (_error) {
    return;
  }
};

export const ChatProvider = ({ children }) => {
  const { resetLiveQuotes } = useLiveQuoteActions();
  // from ChatPage.js
  const [experienceMessageThreads, setExperienceMessageThreads] = useState([]);

  const [showLoadingWaitingMessageByThreadUuid, setShowLoadingWaitingMessageByThreadUuid] = useState({});
  const [useFullWidth, setUseFullWidth] = useState(false);
  const [question, setQuestion] = useState("");
  const [buttonOpacity, setButtonOpacity] = useState(1.0);
  const [preventScroll, setPreventScroll] = useState(false);
  const [loadingResponseByThreadUuid, setLoadingResponseByThreadUuid] = useState({});
  const [opacity, setOpacity] = useState(1.0);
  const [idSelected, setIdSelected] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // When question is submitted; will prevent user from tapping again
  const [preventQuestionInputByThreadUuid, setPreventQuestionInputByThreadUuid] = useState({});
  const [queuedMessagesByThreadUuid, setQueuedMessagesByThreadUuid] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAddHomeModal, setShowAddHomeModal] = useState(false);
  const [thingByThreadUuid, setThingByThreadUuid] = useState({});
  const [testResponseMessageByThreadUuid, setTestResponseMessageByThreadUuid] = useState({});
  const [secondaryExperienceMessage, setSecondaryExperienceMessage] =
    useState(null);
  const [isSecondaryExperience, setIsSecondaryExperience] = useState(null);
  const [secondaryExperienceType, setSecondaryExperienceType] = useState(null);
  const [showSignInButtons, setShowSignInButtons] = useState(false);
  const [questionOnDemandByThreadUuid, setQuestionOnDemandByThreadUuid] = useState({});
  const [showSubOfferModal, setShowSubOfferModal] = useState(null); // starting as null, so when false I trigger check subscription again!
  const [freeUnlimitedOnly, setFreeUnlimitedOnly] = useState(false);
  const [hasShownFreeUnlimitedMessage, setHasShownFreeUnlimitedMessage] =
    useState(false);
  const [deviceType, setDeviceType] = useState("");

  const [loadingPhaseMessageByThreadUuid, setLoadingPhaseMessageByThreadUuid] = useState({});
  const [loadingPhaseDetailByThreadUuid, setLoadingPhaseDetailByThreadUuid] = useState({});
  // const [dataProcessesMessage, setDataProcessesMessage] = useState(null);
  // This had to become a queue because how how many are set at once!! otherwise replaces and not all will show on side data processes panel
  const [dataProcessesQueueByThreadUuid, setDataProcessesQueueByThreadUuid] = useState({});
  const [analysisTraceEventsByThreadUuid, setAnalysisTraceEventsByThreadUuid] = useState({});

  const [liveTextOutputtingByThreadUuid, setLiveTextOutputtingByThreadUuid] = useState({});
  const [activeThread, setActiveThread] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [threadViewLoading, setThreadViewLoading] = useState(false);
  const [loadingMessageThreads, setLoadingMessageThreads] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(null);
  const [isDataProcessesOpen, setIsDataProcessesOpen] = useState(null);
  const [dataProcessesPanelWidth, setDataProcessesPanelWidth] = useState(() => {
    const raw = safeLocalStorageGet(DATA_PROCESSES_PANEL_WIDTH_STORAGE_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [animatedItems, setAnimatedItems] = useState({});
  const [websocketActiveTickerId, setWebsocketActiveTickerIdState] =
    useState(null);
  const [websocketActiveChart, setWebsocketActiveChartState] = useState({
    tickerId: null,
    idx: null,
    scale: null,
  });
  const websocketActiveChartRef = useRef({
    tickerId: null,
    idx: null,
    scale: null,
  });
  const previousLoadingResponseRef = useRef({});
  const [scrollToMessageId, setScrollToMessageId] = useState(null);
  const [unreadThreadUuids, setUnreadThreadUuids] = useState({});

  useEffect(() => {
    websocketActiveChartRef.current = websocketActiveChart;
  }, [websocketActiveChart]);

  useEffect(() => {
    if (!dataProcessesPanelWidth) return;
    safeLocalStorageSet(
      DATA_PROCESSES_PANEL_WIDTH_STORAGE_KEY,
      String(Math.round(dataProcessesPanelWidth)),
    );
  }, [dataProcessesPanelWidth]);

  useEffect(() => {
    const previous = previousLoadingResponseRef.current || {};
    const current = loadingResponseByThreadUuid || {};
    const newlyLoadingThreadUuids = Object.keys(current).filter(
      (threadUuid) => current[threadUuid] && !previous[threadUuid],
    );

    if (newlyLoadingThreadUuids.length > 0) {
      setAnalysisTraceEventsByThreadUuid((prev) => {
        let changed = false;
        const next = { ...(prev || {}) };

        newlyLoadingThreadUuids.forEach((threadUuid) => {
          if (Object.prototype.hasOwnProperty.call(next, threadUuid)) {
            delete next[threadUuid];
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }

    previousLoadingResponseRef.current = current;
  }, [loadingResponseByThreadUuid]);

  const activeThreadUuid = activeThread?.threadUuid ?? null;

  const getThreadValue = (map, threadUuid, fallback) => {
    if (!threadUuid) return fallback;
    if (!map) return fallback;
    return Object.prototype.hasOwnProperty.call(map, threadUuid)
      ? map[threadUuid]
      : fallback;
  };

  const moveThreadEntry = (prev, fromThreadUuid, toThreadUuid) => {
    if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid) {
      return prev;
    }
    if (!Object.prototype.hasOwnProperty.call(prev || {}, fromThreadUuid)) {
      return prev;
    }
    const next = { ...(prev || {}) };
    next[toThreadUuid] = next[fromThreadUuid];
    delete next[fromThreadUuid];
    return next;
  };

  const setThreadLoadingResponse = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    const nextVal = Boolean(value);
    setLoadingResponseByThreadUuid((prev) => {
      if (!nextVal && !Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) {
        return prev;
      }
      if (nextVal && prev?.[threadUuid] === true) return prev;
      if (!nextVal) {
        const next = { ...(prev || {}) };
        delete next[threadUuid];
        return next;
      }
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const setThreadPreventQuestionInput = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    const nextVal = Boolean(value);
    setPreventQuestionInputByThreadUuid((prev) => {
      if (!nextVal && !Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) {
        return prev;
      }
      if (!nextVal) {
        const next = { ...(prev || {}) };
        delete next[threadUuid];
        return next;
      }
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const enqueueQueuedMessageForThread = useCallback((threadUuid, item, options = {}) => {
    if (!threadUuid || !item) return;
    setQueuedMessagesByThreadUuid((prev) =>
      enqueueQueuedMessageForThreadState(prev, threadUuid, item, options).queues,
    );
  }, []);

  const removeQueuedMessageForThread = useCallback((threadUuid, clientQueueId) => {
    if (!threadUuid || !clientQueueId) return;
    setQueuedMessagesByThreadUuid((prev) =>
      removeQueuedMessageForThreadState(prev, threadUuid, clientQueueId).queues,
    );
  }, []);

  const markQueuedMessageForThread = useCallback((threadUuid, clientQueueId, queueStatus, extra = {}) => {
    if (!threadUuid || !clientQueueId || !queueStatus) return;
    setQueuedMessagesByThreadUuid((prev) =>
      markQueuedMessageForThreadState(prev, threadUuid, clientQueueId, queueStatus, extra).queues,
    );
  }, []);

  const updateQueuedMessageForThread = useCallback((threadUuid, clientQueueId, patchOrUpdater = {}) => {
    if (!threadUuid || !clientQueueId) return;
    setQueuedMessagesByThreadUuid((prev) =>
      updateQueuedMessageForThreadState(prev, threadUuid, clientQueueId, patchOrUpdater).queues,
    );
  }, []);

  const shiftQueuedMessageForThread = useCallback((threadUuid, expectedClientQueueId = null) => {
    if (!threadUuid) return;
    setQueuedMessagesByThreadUuid((prev) =>
      shiftQueuedMessageForThreadState(prev, threadUuid, expectedClientQueueId).queues,
    );
  }, []);

  const clearQueuedMessagesForThreadState = useCallback((threadUuid) => {
    if (!threadUuid) return;
    setQueuedMessagesByThreadUuid((prev) =>
      clearQueuedMessagesForThread(prev, threadUuid),
    );
  }, []);

  const setThreadShowLoadingWaitingMessage = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    setShowLoadingWaitingMessageByThreadUuid((prev) => {
      if (prev?.[threadUuid] === value) return prev;
      return { ...(prev || {}), [threadUuid]: value };
    });
  }, []);

  const setThreadLoadingPhaseMessage = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    setLoadingPhaseMessageByThreadUuid((prev) => {
      if (prev?.[threadUuid] === value) return prev;
      return { ...(prev || {}), [threadUuid]: value };
    });
  }, []);

  const setThreadLoadingPhaseDetail = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    setLoadingPhaseDetailByThreadUuid((prev) => {
      if (prev?.[threadUuid] === value) return prev;
      return { ...(prev || {}), [threadUuid]: value };
    });
  }, []);

  const setThreadLiveTextOutputting = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    const nextVal = Boolean(value);
    setLiveTextOutputtingByThreadUuid((prev) => {
      if (!nextVal && !Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) {
        return prev;
      }
      if (!nextVal) {
        const next = { ...(prev || {}) };
        delete next[threadUuid];
        return next;
      }
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const setThreadQuestionOnDemand = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    const nextVal = Boolean(value);
    setQuestionOnDemandByThreadUuid((prev) => {
      if (!nextVal && !Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) {
        return prev;
      }
      if (!nextVal) {
        const next = { ...(prev || {}) };
        delete next[threadUuid];
        return next;
      }
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const setThreadThing = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    const nextVal = Boolean(value);
    setThingByThreadUuid((prev) => {
      if (!nextVal && !Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) {
        return prev;
      }
      if (!nextVal) {
        const next = { ...(prev || {}) };
        delete next[threadUuid];
        return next;
      }
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const setThreadTestResponseMessage = useCallback((threadUuid, value) => {
    if (!threadUuid) return;
    setTestResponseMessageByThreadUuid((prev) => {
      if (prev?.[threadUuid] === value) return prev;
      return { ...(prev || {}), [threadUuid]: value };
    });
  }, []);

  const pushDataProcessForThread = useCallback((threadUuid, text) => {
    if (!threadUuid) return;
    setDataProcessesQueueByThreadUuid((prev) => {
      const current = prev?.[threadUuid] || [];
      const providedId =
        text && typeof text === "object"
          ? text.id || text.event_id || text.eventId
          : null;
      const next = current.concat({
        id: providedId
          ? String(providedId)
          : crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`,
        text,
      });
      return { ...(prev || {}), [threadUuid]: next };
    });
  }, []);

  const clearDataProcessesForThread = useCallback((threadUuid) => {
    if (!threadUuid) return;
    setDataProcessesQueueByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
  }, []);

  const pushAnalysisTraceEventForThread = useCallback((threadUuid, event) => {
    if (!threadUuid || !event) return;
    setAnalysisTraceEventsByThreadUuid((prev) => {
      const current = prev?.[threadUuid] || [];
      const eventId = event.event_id || event.eventId || `${Date.now()}-${Math.random()}`;
      if (current.some((item) => (item.event_id || item.eventId) === eventId)) {
        return prev;
      }
      const next = current.concat({ ...event, event_id: eventId });
      return { ...(prev || {}), [threadUuid]: next };
    });
  }, []);

  const clearThreadState = useCallback((threadUuid) => {
    if (!threadUuid) return;
    setLoadingResponseByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setPreventQuestionInputByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setQueuedMessagesByThreadUuid((prev) =>
      clearQueuedMessagesForThread(prev, threadUuid),
    );
    setShowLoadingWaitingMessageByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setLoadingPhaseMessageByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setLoadingPhaseDetailByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setDataProcessesQueueByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setAnalysisTraceEventsByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setLiveTextOutputtingByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setQuestionOnDemandByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setThingByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
    setTestResponseMessageByThreadUuid((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev || {}, threadUuid)) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
  }, []);

  const moveThreadState = useCallback((fromThreadUuid, toThreadUuid) => {
    if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid) return;
    previousLoadingResponseRef.current = moveThreadEntry(
      previousLoadingResponseRef.current,
      fromThreadUuid,
      toThreadUuid,
    );
    setLoadingResponseByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setPreventQuestionInputByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setQueuedMessagesByThreadUuid((prev) => moveQueuedMessagesForThread(prev, fromThreadUuid, toThreadUuid));
    setShowLoadingWaitingMessageByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setLoadingPhaseMessageByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setLoadingPhaseDetailByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setDataProcessesQueueByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setAnalysisTraceEventsByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setLiveTextOutputtingByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setQuestionOnDemandByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setThingByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
    setTestResponseMessageByThreadUuid((prev) => moveThreadEntry(prev, fromThreadUuid, toThreadUuid));
  }, []);

  const loadingResponse = getThreadValue(loadingResponseByThreadUuid, activeThreadUuid, false);
  const setLoadingResponse = useCallback(
    (value) => setThreadLoadingResponse(activeThreadUuid, value),
    [activeThreadUuid, setThreadLoadingResponse],
  );

  const preventQuestionInput = getThreadValue(preventQuestionInputByThreadUuid, activeThreadUuid, false);
  const setPreventQuestionInput = useCallback(
    (value) => setThreadPreventQuestionInput(activeThreadUuid, value),
    [activeThreadUuid, setThreadPreventQuestionInput],
  );
  const queuedMessages = queuedMessagesForThread(queuedMessagesByThreadUuid, activeThreadUuid);

  const showLoadingWaitingMessage = getThreadValue(showLoadingWaitingMessageByThreadUuid, activeThreadUuid, 0);
  const setShowLoadingWaitingMessage = useCallback(
    (value) => setThreadShowLoadingWaitingMessage(activeThreadUuid, value),
    [activeThreadUuid, setThreadShowLoadingWaitingMessage],
  );

  const loadingPhaseMessage = getThreadValue(loadingPhaseMessageByThreadUuid, activeThreadUuid, null);
  const setLoadingPhaseMessage = useCallback(
    (value) => setThreadLoadingPhaseMessage(activeThreadUuid, value),
    [activeThreadUuid, setThreadLoadingPhaseMessage],
  );

  const loadingPhaseDetail = getThreadValue(loadingPhaseDetailByThreadUuid, activeThreadUuid, null);
  const setLoadingPhaseDetail = useCallback(
    (value) => setThreadLoadingPhaseDetail(activeThreadUuid, value),
    [activeThreadUuid, setThreadLoadingPhaseDetail],
  );

  const liveTextOutputting = getThreadValue(liveTextOutputtingByThreadUuid, activeThreadUuid, false);
  const setLiveTextOutputting = useCallback(
    (value) => setThreadLiveTextOutputting(activeThreadUuid, value),
    [activeThreadUuid, setThreadLiveTextOutputting],
  );

  const questionOnDemand = getThreadValue(questionOnDemandByThreadUuid, activeThreadUuid, false);
  const setQuestionOnDemand = useCallback(
    (value) => setThreadQuestionOnDemand(activeThreadUuid, value),
    [activeThreadUuid, setThreadQuestionOnDemand],
  );

  const thing = getThreadValue(thingByThreadUuid, activeThreadUuid, false);
  const checkThing = useCallback(
    (value) => setThreadThing(activeThreadUuid, value),
    [activeThreadUuid, setThreadThing],
  );

  const testResponseMessage = getThreadValue(testResponseMessageByThreadUuid, activeThreadUuid, "");
  const setTestResponseMessage = useCallback(
    (value) => setThreadTestResponseMessage(activeThreadUuid, value),
    [activeThreadUuid, setThreadTestResponseMessage],
  );

  const dataProcessesQueue = getThreadValue(dataProcessesQueueByThreadUuid, activeThreadUuid, []);
  const pushDataProcess = useCallback(
    (text) => {
      if (!activeThreadUuid) return;
      pushDataProcessForThread(activeThreadUuid, text);
    },
    [activeThreadUuid, pushDataProcessForThread],
  );
  const analysisTraceEvents = getThreadValue(analysisTraceEventsByThreadUuid, activeThreadUuid, []);
  const pushAnalysisTraceEvent = useCallback(
    (event) => {
      if (!activeThreadUuid) return;
      pushAnalysisTraceEventForThread(activeThreadUuid, event);
    },
    [activeThreadUuid, pushAnalysisTraceEventForThread],
  );

  const markThreadUnread = useCallback((threadUuid) => {
    if (!threadUuid) return;
    setUnreadThreadUuids((prev) => {
      if (prev?.[threadUuid]) return prev;
      return { ...(prev || {}), [threadUuid]: true };
    });
  }, []);

  const markThreadRead = useCallback((threadUuid) => {
    if (!threadUuid) return;
    setUnreadThreadUuids((prev) => {
      if (!prev?.[threadUuid]) return prev;
      const next = { ...(prev || {}) };
      delete next[threadUuid];
      return next;
    });
  }, []);

  const syncUnreadThreadUuids = useCallback((threadsList = [], options = {}) => {
    const { merge = false } = options || {};
    const next = {};
    (threadsList || []).forEach((t) => {
      if (t?.hasUnread && t?.threadUuid) next[t.threadUuid] = true;
    });
    setUnreadThreadUuids((prev) => (merge ? { ...(prev || {}), ...next } : next));
  }, []);

  const setWebsocketActiveTickerId = useCallback((tickerId) => {
    const normalizedTickerId =
      tickerId == null || tickerId === "" ? null : String(tickerId);
    setWebsocketActiveTickerIdState(normalizedTickerId);
    setWebsocketActiveChartState((prev) => {
      if ((prev?.tickerId ?? null) === normalizedTickerId) return prev;
      return {
        tickerId: normalizedTickerId,
        idx: prev?.idx ?? null,
        scale: prev?.scale ?? null,
      };
    });
  }, []);

  const setWebsocketActiveChart = useCallback((next) => {
    const normalized = {
      tickerId:
        next?.tickerId == null || next?.tickerId === ""
          ? null
          : String(next.tickerId),
      idx: next?.idx == null || next?.idx === "" ? null : String(next.idx),
      scale:
        next?.scale == null || next?.scale === ""
          ? null
          : String(next.scale),
    };

    setWebsocketActiveTickerIdState(normalized.tickerId);
    setWebsocketActiveChartState((prev) => {
      if (
        (prev?.tickerId ?? null) === normalized.tickerId &&
        (prev?.idx ?? null) === normalized.idx &&
        (prev?.scale ?? null) === normalized.scale
      ) {
        return prev;
      }
      return normalized;
    });
  }, []);
  
  const resetChatState = useCallback(() => {
    clearRememberedActiveChatThread();
    setExperienceMessageThreads([]);
    setShowLoadingWaitingMessageByThreadUuid({});
    setUseFullWidth(false);
    setQuestion("");
    setButtonOpacity(1.0);
    setPreventScroll(false);
    setLoadingResponseByThreadUuid({});
    setOpacity(1.0);
    setIdSelected(null);
    setShowScrollDown(false);
    setPreventQuestionInputByThreadUuid({});
    setQueuedMessagesByThreadUuid({});
    setShowTermsModal(false);
    setShowAddHomeModal(false);
    setThingByThreadUuid({});
    setTestResponseMessageByThreadUuid({});
    setSecondaryExperienceMessage(null);
    setIsSecondaryExperience(null);
    setSecondaryExperienceType(null);
    setShowSignInButtons(false);
    setQuestionOnDemandByThreadUuid({});
    setShowSubOfferModal(null);
    setFreeUnlimitedOnly(false);
    setHasShownFreeUnlimitedMessage(false);
    setDeviceType("");
    setLoadingPhaseMessageByThreadUuid({});
    setLoadingPhaseDetailByThreadUuid({});
    setDataProcessesQueueByThreadUuid({});
    setLiveTextOutputtingByThreadUuid({});
    setActiveThread(null);
    setThreads([]);
    setLoadingMessages(false);
    setThreadViewLoading(false);
    setLoadingMessageThreads(false)
    setIsSidebarOpen(null);
    resetLiveQuotes();
    setAnimatedItems({});
    setWebsocketActiveTickerIdState(null);
    setWebsocketActiveChartState({
      tickerId: null,
      idx: null,
      scale: null,
    });
    setScrollToMessageId(null);
    setUnreadThreadUuids({});
  }, [resetLiveQuotes]);

  const value = {
    experienceMessageThreads,
    setExperienceMessageThreads,
    showLoadingWaitingMessage,
    setShowLoadingWaitingMessage,
    showLoadingWaitingMessageByThreadUuid,
    setThreadShowLoadingWaitingMessage,
    useFullWidth,
    setUseFullWidth,
    question,
    setQuestion,
    buttonOpacity,
    setButtonOpacity,
    preventScroll,
    setPreventScroll,
    loadingResponse,
    setLoadingResponse,
    loadingResponseByThreadUuid,
    setThreadLoadingResponse,
    opacity,
    setOpacity,
    idSelected,
    setIdSelected,
    showScrollDown,
    setShowScrollDown,
    preventQuestionInput,
    setPreventQuestionInput,
    preventQuestionInputByThreadUuid,
    setThreadPreventQuestionInput,
    queuedMessages,
    queuedMessagesByThreadUuid,
    enqueueQueuedMessageForThread,
    removeQueuedMessageForThread,
    markQueuedMessageForThread,
    updateQueuedMessageForThread,
    shiftQueuedMessageForThread,
    clearQueuedMessagesForThread: clearQueuedMessagesForThreadState,
    showTermsModal,
    setShowTermsModal,
    showAddHomeModal,
    setShowAddHomeModal,
    thing,
    checkThing,
    thingByThreadUuid,
    setThreadThing,
    testResponseMessage,
    setTestResponseMessage,
    testResponseMessageByThreadUuid,
    setThreadTestResponseMessage,
    secondaryExperienceMessage,
    setSecondaryExperienceMessage,
    isSecondaryExperience,
    setIsSecondaryExperience,
    secondaryExperienceType,
    setSecondaryExperienceType,
    showSignInButtons,
    setShowSignInButtons,
    questionOnDemand,
    setQuestionOnDemand,
    questionOnDemandByThreadUuid,
    setThreadQuestionOnDemand,
    showSubOfferModal,
    setShowSubOfferModal,
    freeUnlimitedOnly,
    setFreeUnlimitedOnly,
    hasShownFreeUnlimitedMessage,
    setHasShownFreeUnlimitedMessage,
    deviceType,
    setDeviceType,
    loadingPhaseMessage,
    setLoadingPhaseMessage,
    loadingPhaseMessageByThreadUuid,
    setThreadLoadingPhaseMessage,
    loadingPhaseDetail,
    setLoadingPhaseDetail,
    loadingPhaseDetailByThreadUuid,
    setThreadLoadingPhaseDetail,
    // dataProcessesMessage,
    // setDataProcessesMessage,
    dataProcessesQueue,
    pushDataProcess,
    dataProcessesQueueByThreadUuid,
    pushDataProcessForThread,
    clearDataProcessesForThread,
    analysisTraceEvents,
    pushAnalysisTraceEvent,
    analysisTraceEventsByThreadUuid,
    pushAnalysisTraceEventForThread,
    liveTextOutputting,
    setLiveTextOutputting,
    liveTextOutputtingByThreadUuid,
    setThreadLiveTextOutputting,
    activeThread,
    setActiveThread,
    threads,
    setThreads,
    loadingMessages,
    setLoadingMessages,
    threadViewLoading,
    setThreadViewLoading,
    isSidebarOpen,
    setIsSidebarOpen,
    isDataProcessesOpen, setIsDataProcessesOpen,
    dataProcessesPanelWidth, setDataProcessesPanelWidth,
    animatedItems, setAnimatedItems,
    resetChatState,
    websocketActiveTickerId, setWebsocketActiveTickerId,
    websocketActiveChart, setWebsocketActiveChart, websocketActiveChartRef,
    loadingMessageThreads, setLoadingMessageThreads,
    scrollToMessageId, setScrollToMessageId,
    unreadThreadUuids, markThreadUnread, markThreadRead, syncUnreadThreadUuids,
    moveThreadState,
    clearThreadState,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => useContext(ChatContext);
